'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, Padding, Typography } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { createCurationsService } from '@musinsa/services-curator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Controller, useFormContext } from 'react-hook-form';
import { FloatingButtonGroup, ScrollToTopButton, useCuratorBroadcastPublisher } from '../common';
import { BottomButtonWrapper } from '../common/components/BottomButtonWrapper';
import { useCuratorSnackbar, useNavigate } from '../common/hooks';
import { SearchedProduct } from '../product/components/variants/SearchedProduct';
import { buildSelectItemGtmAttr } from '../product/utils/buildSelectItemGtmAttr';
import { curatorQueries } from '../query-factory';
import { LabeledTextField } from './LabeledTextField';
import type { ShopCurationEditFormData } from './ShopGoodsCurationEdit';

interface Props {
  className?: string;
  onClickSelectGoods: () => void;
}

export function CreateGoodsCurationForm({ className, onClickSelectGoods }: Props) {
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';
  const snackbar = useCuratorSnackbar();
  const dialog = useDialog();
  const { navigateBack } = useNavigate();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useFormContext<ShopCurationEditFormData>();
  const { triggerQuerySync } = useCuratorBroadcastPublisher();

  const { mutate: createCuration, isPending } = useMutation({
    mutationFn: createCurationsService,
    onSuccess: ({ curationId }) => {
      if (service === SERVICE.MUSINSA && curatorId) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'curation_upload_complete',
          component_name: 'button',
          event_name: 'click_button',
          from: getScreenName(),
          id: curatorId,
          section_name: 'curator_curationfeed',
          type: 'goods',
        });
      }
      triggerQuerySync({
        queryKeys: [
          curatorQueries.curations.detail({ curationId }).queryKey,
          curatorQueries.curations.myList._def,
          curatorQueries.curations.checkCurationCreatable().queryKey,
        ],
      });
      queryClient.removeQueries({
        queryKey: curatorQueries.curations.myList._def,
      });
      snackbar.success('큐레이션이 등록되었습니다.');
      navigateBack();
    },
    onError: () => {
      snackbar.error('큐레이션이 등록에 실패했습니다.');
    },
  });

  const onSubmit = () => {
    const { title, description, selectedProducts } = getValues();

    if (selectedProducts.length === 0) {
      dialog.open({
        title: '상품을 선택해야 큐레이션 등록이 가능합니다.',
        confirmButtonText: '확인',
        onConfirm: () => dialog.close(),
      });
      return;
    }

    createCuration({
      title,
      description: description ?? '',
      goodsNoList: selectedProducts.map((product) => String(product.goodsNo)),
      curationType: 'GOODS',
    });
  };

  const watchTitle = watch('title');
  const watchDescription = watch('description');
  const watchSelectedProducts = watch('selectedProducts');

  return (
    <form
      className={clsx('w-full grow flex flex-col', className)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="grow z-[0] pb-[72px]!">
        <div className="flex flex-col gap-1 bg-white px-4 pt-2 pb-3">
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <LabeledTextField
                label="제목 (필수)"
                maxLength={25}
                error={errors.title?.message}
                onClear={() => setValue('title', '')}
                count={watchTitle.length}
                placeholder="간절기 아우터 추천"
                {...field}
              />
            )}
          />
          <LabeledTextField
            label="설명 (선택)"
            maxLength={18}
            onClear={() => setValue('description', '')}
            count={watchDescription?.length}
            placeholder="50% 할인 상품 모음"
            {...register('description')}
          />
        </div>

        <div className="flex flex-col gap-2 px-4 pt-3 pb-2">
          <Typography variant="body14pxMed">
            큐레이션 상품
            {watchSelectedProducts.length > 0 && ` (${watchSelectedProducts.length}개)`}
          </Typography>
        </div>
        <div className="flex justify-center px-4 pb-3">
          <Button
            type="button"
            variant="secondary"
            size={40}
            className="w-full"
            onClick={() => {
              if (service === SERVICE.MUSINSA && curatorId) {
                amplitudeSendEvent({
                  action_name: 'click',
                  button_name: 'goods_add_try',
                  component_name: 'button',
                  event_name: 'click_button',
                  from: getScreenName(),
                  id: curatorId,
                  section_name: 'curator_curation_create',
                  type: 'goods',
                });
              }
              onClickSelectGoods();
            }}
          >
            <Typography variant="body14pxMed">상품 선택</Typography>
          </Button>
        </div>
        {watchSelectedProducts.length > 0 && (
          <div className="grid w-full grid-cols-3 px-0 bg-neutral-color-white">
            {watchSelectedProducts.map((product, index) => (
              <SearchedProduct
                key={product.goodsNo}
                item={product}
                index={index}
                {...(service === SERVICE.MUSINSA ? buildSelectItemGtmAttr(product, index) : {})}
                onClickCloseButton={() => {
                  const newProducts = watchSelectedProducts.filter(
                    (item) => item.goodsNo !== product.goodsNo,
                  );
                  setValue('selectedProducts', newProducts);
                }}
              />
            ))}
          </div>
        )}
      </div>
      <BottomButtonWrapper>
        <FloatingButtonGroup.Container
          className="absolute -top-12 right-0"
          style={{
            transform: 'translateY(-100%)',
          }}
        >
          <ScrollToTopButton />
        </FloatingButtonGroup.Container>
        <Padding y="3">
          <Button type="submit" size="44" className="w-full" disabled={isPending}>
            <Typography size="14" weight="medium">
              등록하기
            </Typography>
          </Button>
        </Padding>
      </BottomButtonWrapper>
    </form>
  );
}
